/**
 * NewWorkPage — éditeur d'œuvre Direction C « Fiche conservateur ».
 *
 * Layout 60/40 : à gauche un cartel-de-musée live (auteur · titre italique ·
 * filet · technique/date · story · description · moods), à droite un
 * formulaire numéroté en 5 blocs (01 Identité, 02 Photographies,
 * 03 Intention, 04 Catalogage, 05 Notes d'atelier).
 *
 * Une seule page pour deux modes :
 *   - `/works/new`     → mode création. Form vide. Au 1er commit valide du
 *     titre (debounce 800 ms), POST /api/works → navigate replace
 *     `/works/:id/edit`. Plus de bouton « Créer ».
 *   - `/works/:id/edit` → mode édition. Prefill via `useWork(id)`. Toute
 *     modification de champ → debounce 800 ms → PATCH /api/works/:id sur
 *     le diff.
 *
 * Indicateur d'enregistrement + progression dans `<Page.Header meta>` :
 *   « Brouillon enregistré · à l'instant » + barre segmentée N/6.
 *
 * Fichiers : la route POST /api/works ne porte plus les images. L'utilisateur
 * peut déposer des fichiers avant ou après la 1re sauvegarde — les fichiers
 * déposés en mode création sont bufferisés et flushés via POST /works/:id/media
 * dès que `workId` existe. Vision Tier 1 est déclenché à l'ajout du 1er
 * média (et pas avant).
 *
 * Tags libres : en mode édition, le diff entre l'état précédent et l'état
 * courant déclenche attach (POST /works/:id/tags) ou detach (DELETE
 * /works/:id/tags/:tagId). En mode création, sont passés à l'INSERT initial.
 */

import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
  type DefaultValues,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useParams } from 'react-router'
import { ArrowLeft, ImageIcon, Plus, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Button from '@/components/ui/Button'
import ChipMultiSelect from '@/components/ui/ChipMultiSelect'
import Field from '@/components/ui/Field'
import FileDropZone from '@/components/ui/FileDropZone'
import KebabMenu, { type KebabMenuItem } from '@/components/ui/KebabMenu'
import MediaImage from '@/components/ui/MediaImage'
import Modal from '@/components/ui/Modal'
import SeriesCreateModal from '@/components/work/SeriesCreateModal'
import SeriesReorderPanel from '@/components/work/SeriesReorderPanel'
import Page from '@/components/layout/Page'
import Select from '@/components/ui/Select'
import TagPicker, { type Tag } from '@/components/ui/TagPicker'
import Textarea from '@/components/ui/Textarea'
import FormField from '@/components/forms/FormField'
import FieldThresholdHint from '@/components/forms/FieldThresholdHint'
import FormSection from '@/components/forms/FormSection'
import FormTextarea from '@/components/forms/FormTextarea'
import ProofreadHint from '@/components/forms/ProofreadHint'
import {
  PORTFOLIO_PUBLISH_DESCRIPTION_FR_MIN_CHARS,
  PORTFOLIO_PUBLISH_STORY_FR_MIN_CHARS,
} from '@/schemas/works'
import { useRephraseField } from '@/hooks/useDrafts'
import {
  useAttachTags,
  useCreateSeries,
  useCreateWork,
  useDeleteMedia,
  useDetachTag,
  useSeriesList,
  useSeriesWorks,
  useUpdateWork,
  useVisionJob,
  useWork,
  worksKeys,
} from '@/hooks/useWorks'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useBulkUploadStack } from '@/contexts/bulk-upload-stack-context'
import { worksApi } from '@/utils/api/works'
import { useQueryClient } from '@tanstack/react-query'
import { tagsApi } from '@/utils/api/tags'
import {
  workEditorFormSchema,
  type WorkEditorForm,
  type WorkEditorFormParsed,
} from '@/schemas/works'
import { useEmotionalIntents, useTargetAudiences } from '@/hooks/useCatalog'
import type { TagResponse, WorkResponse } from '@/types/works'

import './NewWorkPage.css'

const ACCEPTED_FILE_EXTENSIONS =
  'image/jpeg,image/x-canon-cr2,image/x-canon-cr3,image/x-nikon-nef,image/x-sony-arw,image/x-adobe-dng,image/x-fuji-raf,image/x-olympus-orf,.jpg,.jpeg,.cr2,.cr3,.nef,.arw,.dng,.raf,.orf'

const AUTOSAVE_DEBOUNCE_MS = 800

/** Nombre total d'éléments à renseigner — affiché en barre segmentée. */
const COMPLETION_TOTAL = 6

function tagResponseToTag(t: TagResponse): Tag {
  return { id: t.id, slug: t.slug, label: t.label_fr, kind: t.kind }
}

const EMPTY_DEFAULTS: DefaultValues<WorkEditorForm> = {
  title_fr: '',
  series_id: undefined,
  series_position: undefined,
  description_fr: '',
  story_fr: '',
  emotional_intent: [],
  target_audience: [],
  technique_tags: [],
  influences: [],
  notes: '',
  created_at_artistic: '',
  tag_ids: [],
}

function workToFormDefaults(work: WorkResponse): DefaultValues<WorkEditorForm> {
  return {
    title_fr: work.title_fr ?? '',
    series_id: work.series_id ?? undefined,
    series_position: work.series_position ?? undefined,
    description_fr: work.description_fr ?? '',
    story_fr: work.story_fr ?? '',
    emotional_intent: work.emotional_intent ?? [],
    target_audience: work.target_audience ?? [],
    technique_tags: work.technique_tags ?? [],
    influences: work.influences ?? [],
    notes: work.notes ?? '',
    created_at_artistic: work.created_at_artistic ?? '',
    tag_ids: (work.tags ?? []).map((t) => t.id),
  }
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

/**
 * Diff naïf entre deux objets — renvoie les clés dont la valeur a changé.
 * Compare via JSON.stringify (suffisant pour scalars/arrays/objets simples).
 */
function diffKeys<T extends Record<string, unknown>>(prev: T, next: T): Array<keyof T> {
  const keys = new Set<string>([...Object.keys(prev), ...Object.keys(next)])
  const changed: Array<keyof T> = []
  for (const k of keys) {
    if (JSON.stringify(prev[k as keyof T]) !== JSON.stringify(next[k as keyof T])) {
      changed.push(k as keyof T)
    }
  }
  return changed
}

export default function NewWorkPage() {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()

  const initialId = params.id ? Number(params.id) : null
  const isEditMode = initialId !== null && Number.isFinite(initialId) && initialId > 0
  const [workId, setWorkId] = useState<number | null>(isEditMode ? initialId : null)

  const seriesQuery = useSeriesList()
  const workQuery = useWork(workId)
  const visionJobQuery = useVisionJob(workId)
  // Tier 2 — fetch des slugs paramétrables (S2.5c). `only_active` masque les
  // entrées désactivées dans Réglages mais pas celles déjà référencées par
  // l'œuvre courante (le ChipMultiSelect garde la sélection telle quelle).
  const emotionalIntentsQuery = useEmotionalIntents({ only_active: true })
  const targetAudiencesQuery = useTargetAudiences({ only_active: true })

  const createMutation = useCreateWork()
  const updateMutation = useUpdateWork(workId ?? 0)
  const attachTagsMutation = useAttachTags(workId ?? 0)
  const detachTagMutation = useDetachTag(workId ?? 0)
  const rephraseMutation = useRephraseField()

  const qc = useQueryClient()
  const { enqueue } = useBulkUploadStack()

  // ── Form ─────────────────────────────────────────────────────────────────
  const methods = useForm<WorkEditorForm, unknown, WorkEditorFormParsed>({
    resolver: zodResolver(workEditorFormSchema),
    defaultValues: EMPTY_DEFAULTS,
    mode: 'onBlur',
  })
  const { control, getValues, reset } = methods

  // ── State ────────────────────────────────────────────────────────────────
  const [extraTags, setExtraTags] = useState<Tag[]>([])
  /**
   * Files droppés en mode création (workId === null) — bufferisés tant que
   * l'œuvre n'a pas été POSTée. Une fois workId disponible, l'effet
   * ci-dessous flush ce buffer en `enqueue` vers le provider d'upload
   * global. En mode édition, on enqueue directement sans passer par ici.
   */
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [hydrated, setHydrated] = useState(!isEditMode)
  const [rephraseOpen, setRephraseOpen] = useState<null | {
    field: 'story_fr' | 'description_fr' | 'notes'
    label: string
  }>(null)
  const [rephraseVariants, setRephraseVariants] = useState<string[]>([])
  const [rephraseCorrection, setRephraseCorrection] = useState<{
    text: string
    hasChanges: boolean
  } | null>(null)
  // Hint orthographe — état "corrections suggérées" par champ, remonté par
  // <ProofreadHint> pour teinter le wrapper textarea en rouge subtil.
  const [storyHasCorrections, setStoryHasCorrections] = useState(false)
  const [descriptionHasCorrections, setDescriptionHasCorrections] = useState(false)
  const [seriesModalOpen, setSeriesModalOpen] = useState(false)

  const createSeriesMutation = useCreateSeries()

  // Snapshot des dernières valeurs persistées — sert au diff PATCH.
  const lastSavedRef = useRef<WorkEditorFormParsed | null>(null)
  const lastSavedTagIdsRef = useRef<number[]>([])

  // ── Hydratation : prefill quand `useWork` répond en mode édition ────────
  useEffect(() => {
    if (!isEditMode) return
    if (hydrated) return
    if (!workQuery.data) return

    const defaults = workToFormDefaults(workQuery.data)
    reset(defaults, { keepDirty: false })

    const tags = workQuery.data.tags ?? []
    // Hydratation one-shot depuis le serveur (gardée par `hydrated`) — la
    // règle React Compiler `set-state-in-effect` est conçue pour le steady
    // state, pas pour le prefill initial qui ne peut pas vivre en useMemo
    // (l'utilisateur édite ensuite `extraTags` localement).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExtraTags(tags.map(tagResponseToTag))
    lastSavedTagIdsRef.current = tags.map((t) => t.id)

    // Snapshot initial pour le diff — on parse via le schéma pour aligner
    // les types numériques (Decimal) avec ce que le BE renverra.
    const parsed = workEditorFormSchema.safeParse(getValues())
    if (parsed.success) {
      lastSavedRef.current = parsed.data
    }
    setHydrated(true)
  }, [isEditMode, hydrated, workQuery.data, reset, getValues])

  // ── Helpers status ───────────────────────────────────────────────────────
  const markSaved = useCallback(() => {
    setStatus('saved')
    setLastSavedAt(new Date())
  }, [])

  // ── Création différée : POST /api/works dès que title_fr devient valide ─
  const createDraftIfPossible = useCallback(
    async (parsed: WorkEditorFormParsed): Promise<number | null> => {
      if (workId !== null) return workId
      if (!parsed.title_fr || parsed.title_fr.trim().length === 0) return null
      // Anti-doublon : si une création est déjà en vol ou a échoué, on ne
      // re-fire pas — le BE peut avoir inséré la row malgré une 500 sur la
      // sérialisation, et l'utilisateur se retrouverait avec N œuvres
      // identiques. L'utilisateur reset via refresh.
      if (createMutation.isPending || createMutation.isError) return null

      try {
        setStatus('saving')
        const created = await createMutation.mutateAsync({
          title_fr: parsed.title_fr,
          series_id: parsed.series_id ?? null,
          series_position: parsed.series_position ?? null,
          description_fr: parsed.description_fr ?? null,
          story_fr: parsed.story_fr ?? null,
          emotional_intent: parsed.emotional_intent ?? null,
          target_audience: parsed.target_audience ?? null,
          technique_tags: parsed.technique_tags ?? null,
          influences: parsed.influences ?? null,
          notes: parsed.notes ?? null,
          created_at_artistic: parsed.created_at_artistic ? parsed.created_at_artistic : null,
          tag_ids: extraTags.map((t) => Number(t.id)),
        })
        lastSavedRef.current = parsed
        lastSavedTagIdsRef.current = extraTags.map((t) => Number(t.id))
        setWorkId(created.id)
        markSaved()
        navigate(`/works/${created.id}/edit`, { replace: true })
        return created.id
      } catch (err) {
        const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
        toast.error(`Création impossible : ${detail}`)
        setStatus('error')
        return null
      }
    },
    [workId, createMutation, extraTags, markSaved, navigate],
  )

  // ── PATCH du diff (mode édition) ─────────────────────────────────────────
  const patchDiff = useCallback(
    async (parsed: WorkEditorFormParsed) => {
      if (workId === null) return
      const prev = lastSavedRef.current
      if (prev === null) {
        // Premier patch après prefill — on snapshot et on ne PATCH rien.
        lastSavedRef.current = parsed
        return
      }
      const changed = diffKeys(
        prev as unknown as Record<string, unknown>,
        parsed as unknown as Record<string, unknown>,
      )
      if (changed.length === 0) return

      const payload: Record<string, unknown> = {}
      for (const key of changed) {
        const k = key as keyof WorkEditorFormParsed
        const value = parsed[k]
        // Empty string → null (le BE distingue les clears via Pydantic).
        payload[k as string] = value === '' ? null : value === undefined ? null : value
      }

      try {
        setStatus('saving')
        const updated = await updateMutation.mutateAsync(payload)
        // Sync série/position depuis la réponse — le BE auto-assigne la
        // position quand series_id change (Option C : positions managées,
        // pas saisies par l'utilisateur).
        if ('series_id' in payload) {
          methods.setValue('series_position', updated.series_position ?? undefined, {
            shouldDirty: false,
          })
          parsed.series_position = updated.series_position ?? undefined
        }
        lastSavedRef.current = parsed
        markSaved()
      } catch (err) {
        const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
        toast.error(`Enregistrement impossible : ${detail}`)
        setStatus('error')
      }
    },
    [workId, updateMutation, markSaved, methods],
  )

  // ── Sync diff des tags libres (mode édition) ─────────────────────────────
  const syncTagsDiff = useCallback(
    async (currentTagIds: number[]) => {
      if (workId === null) return
      const prev = lastSavedTagIdsRef.current
      const prevSet = new Set(prev)
      const nextSet = new Set(currentTagIds)
      const toAttach = currentTagIds.filter((id) => !prevSet.has(id))
      const toDetach = prev.filter((id) => !nextSet.has(id))

      if (toAttach.length === 0 && toDetach.length === 0) return

      try {
        setStatus('saving')
        if (toDetach.length > 0) {
          for (const tagId of toDetach) {
            await detachTagMutation.mutateAsync(tagId)
          }
        }
        if (toAttach.length > 0) {
          await attachTagsMutation.mutateAsync(toAttach)
        }
        lastSavedTagIdsRef.current = [...currentTagIds]
        markSaved()
      } catch (err) {
        const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
        toast.error(`Tags : ${detail}`)
        setStatus('error')
      }
    },
    [workId, attachTagsMutation, detachTagMutation, markSaved],
  )

  // ── Boucle d'autosave debounce ───────────────────────────────────────────
  // On observe TOUTES les valeurs du formulaire ; à chaque changement, on
  // (re)démarre un timer de 800 ms ; à expiration, on parse via Zod et :
  //   - mode création : tente createDraft (si title_fr valide)
  //   - mode édition  : PATCH le diff
  // `useWatch` (vs `methods.watch()`) → compatible React Compiler memoization.
  const watchedValues = useWatch({ control })
  const tagIdsForSync = useMemo(
    () => extraTags.map((t) => Number(t.id)).sort((a, b) => a - b),
    [extraTags],
  )

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (isEditMode && !hydrated) return // wait for prefill

    timerRef.current = setTimeout(() => {
      const parseResult = workEditorFormSchema.safeParse(getValues())
      if (!parseResult.success) {
        // Form invalide pour l'instant — silencieux (Zod errors affichés inline).
        return
      }
      const parsed = parseResult.data

      if (workId === null) {
        void createDraftIfPossible(parsed)
      } else {
        void patchDiff(parsed)
        void syncTagsDiff(tagIdsForSync)
      }
    }, AUTOSAVE_DEBOUNCE_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [
    watchedValues,
    tagIdsForSync,
    isEditMode,
    hydrated,
    workId,
    getValues,
    createDraftIfPossible,
    patchDiff,
    syncTagsDiff,
  ])

  // ── Files : enqueue dans la pile globale d'uploads (Drive-style) ────────
  // `concurrencyLimit: 1` — un RAW pèse 100-200 Mo, la bande passante upload
  // est le facteur limitant. À 2 en parallèle on /2 le débit par fichier
  // sans gain de wall-clock. Le provider survit aux changements de route :
  // l'utilisatrice peut continuer à travailler ailleurs pendant l'upload.
  const enqueueForWork = useCallback(
    (wid: number, files: File[]) => {
      enqueue({
        label: `Œuvre #${wid} — ${files.length} image${files.length > 1 ? 's' : ''}`,
        files,
        uploader: (file) => worksApi.addMediaOne(wid, file),
        concurrencyLimit: 1,
        onComplete: () => {
          void qc.invalidateQueries({ queryKey: worksKeys.detail(wid) })
          void qc.invalidateQueries({ queryKey: worksKeys.lists() })
          markSaved()
        },
      })
    },
    [enqueue, qc, markSaved],
  )

  // ── Files : flush le buffer dès qu'un workId est dispo ───────────────────
  // Pattern queue-drain : `pendingFiles` accumule pendant que `workId` est
  // null, puis on enqueue dans la pile globale dès qu'il existe.
  useEffect(() => {
    if (workId === null) return
    if (pendingFiles.length === 0) return
    enqueueForWork(workId, pendingFiles)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPendingFiles([])
  }, [workId, pendingFiles, enqueueForWork])

  // ── Drop handlers ────────────────────────────────────────────────────────
  // Mode édition (workId !== null) : on enqueue direct dans la pile
  // globale. Mode création (workId === null) : la dropzone est branchée
  // directement sur `pendingFiles` (cf rendu plus bas) — `handleFilesChange`
  // n'est appelé qu'en mode édition pour enqueue immédiat.
  const handleFilesChange = (files: File[]) => {
    if (files.length === 0) return
    if (workId === null) return // garde-fou — mode création utilise setPendingFiles direct
    // Mode édition — enqueue direct dans la pile globale (1 fichier = 1
    // requête, progress visible per-file dans l'overlay bas-droit). Avant ce
    // refactor c'était une mutation directe en multipart 10-files qui
    // laissait Stéphanie devant une fenêtre figée.
    enqueueForWork(workId, files)
  }

  // ── Rephrase (Aide-moi à formuler) ───────────────────────────────────────
  // 4 cartes dans la modale : carte "Corrigé" (orthographe/grammaire/typo
  // sans changement de style) en 1er, puis 2-3 reformulations.
  const openRephrase = async (field: 'story_fr' | 'description_fr' | 'notes', label: string) => {
    const rawInput = methods.getValues(field) ?? ''
    if (!rawInput || rawInput.trim().length < 3) {
      toast.error("Écris d'abord quelques mots — Claude reformule, n'invente pas.")
      return
    }
    setRephraseVariants([])
    setRephraseCorrection(null)
    setRephraseOpen({ field, label })
    try {
      const result = await rephraseMutation.mutateAsync({
        field,
        raw_input: rawInput,
        work_title_fr: methods.getValues('title_fr') || null,
        work_technique_tags: methods.getValues('technique_tags') ?? null,
        work_emotional_intent: methods.getValues('emotional_intent') ?? null,
      })
      setRephraseVariants(result.variants)
      setRephraseCorrection({
        text: result.correction,
        hasChanges: result.has_correction_changes,
      })
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Reformulation impossible : ${detail}`)
      setRephraseOpen(null)
    }
  }

  const acceptRephrase = (variant: string) => {
    if (!rephraseOpen) return
    methods.setValue(rephraseOpen.field, variant, {
      shouldDirty: true,
      shouldValidate: true,
    })
    setRephraseOpen(null)
    setRephraseVariants([])
    setRephraseCorrection(null)
  }

  // Ouvre la modale "Aide-moi" sur un champ et précharge la correction
  // (déjà connue via le ProofreadHint inline) — évite un 2e appel Claude
  // pour la même valeur.
  const openCorrectionFromHint = (
    field: 'story_fr' | 'description_fr' | 'notes',
    label: string,
    correction: string,
  ) => {
    setRephraseVariants([])
    setRephraseCorrection({ text: correction, hasChanges: true })
    setRephraseOpen({ field, label })
  }

  // ── Status indicator ─────────────────────────────────────────────────────
  // `Date.now()` est volontairement impur ici : le useEffect ci-dessous
  // re-déclenche un render toutes les 15 s (en mutant `lastSavedAt`) pour
  // rafraîchir l'affichage « il y a Xs ». La règle `purity` ne peut pas
  // détecter ce couplage intentionnel.
  const statusLabel = useMemo(() => {
    if (status === 'saving') return 'Enregistrement…'
    if (status === 'error') return 'Erreur d’enregistrement'
    if (status === 'saved' && lastSavedAt) {
      // eslint-disable-next-line react-hooks/purity
      const seconds = Math.max(0, Math.floor((Date.now() - lastSavedAt.getTime()) / 1000))
      if (seconds < 5) return 'Brouillon enregistré · à l’instant'
      if (seconds < 60) return `Brouillon enregistré · il y a ${seconds}s`
      const minutes = Math.floor(seconds / 60)
      return `Brouillon enregistré · il y a ${minutes} min`
    }
    return ''
  }, [status, lastSavedAt])

  // Force un re-render léger toutes les 15s pour rafraîchir « il y a Xs ».
  useEffect(() => {
    if (status !== 'saved') return
    const t = setInterval(() => {
      // Trigger re-render en re-settant `lastSavedAt` à la même valeur.
      setLastSavedAt((d) => (d ? new Date(d.getTime()) : d))
    }, 15_000)
    return () => clearInterval(t)
  }, [status])

  const seriesOptions = seriesQuery.data ?? []
  const visionJob = visionJobQuery.data
  const visionPending = visionJob?.status === 'pending' || visionJob?.status === 'running'

  const emotionalIntentOptions = useMemo(
    () =>
      (emotionalIntentsQuery.data ?? []).map((o) => ({
        value: o.slug,
        label: o.label_fr,
      })),
    [emotionalIntentsQuery.data],
  )
  const targetAudienceOptions = useMemo(
    () =>
      (targetAudiencesQuery.data ?? []).map((o) => ({
        value: o.slug,
        label: o.label_fr,
      })),
    [targetAudiencesQuery.data],
  )

  // Médias déjà chargés (mode édition ou création après 1er upload). Même
  // règle que `WorkDetailPage` : on privilégie `display`, fallback `thumb`.
  const workMedia = workQuery.data?.media ?? []
  const displayMedia = workMedia.filter((m) => m.kind === 'display')
  const thumbMedia = workMedia.filter((m) => m.kind === 'thumb')
  const galleryMedia = displayMedia.length > 0 ? displayMedia : thumbMedia
  const heroMedia = galleryMedia[0] ?? null

  // Suppression d'un ratio (typiquement upload raté). On supprime le source
  // parent — `source_media_id` du display/thumb pointe vers le row source ;
  // les dérivés cascadent via FK.
  const deleteMediaMutation = useDeleteMedia(workId ?? 0)
  const { confirm } = useConfirm()
  const handleDeleteMedia = async (sourceId: number, idx: number) => {
    const ok = await confirm({
      title: 'Supprimer cette photographie ?',
      message:
        `La photographie n°${idx + 1} sera retirée de l'œuvre. Action ` +
        'irréversible — les fichiers display + thumb dérivés sont également purgés.',
      variant: 'danger',
      confirmText: 'Supprimer définitivement',
    })
    if (!ok) return
    deleteMediaMutation.mutate(sourceId, {
      onSuccess: () => toast.success('Photographie supprimée'),
      onError: (err) => toast.error(err.detail ?? 'Suppression impossible'),
    })
  }

  // ── Loading state (mode édition) ─────────────────────────────────────────
  if (isEditMode && !hydrated) {
    return (
      <Page width="wide">
        <Page.Header
          eyebrow={
            <Button variant="quiet" size="sm" type="button" onClick={() => navigate('/works')}>
              <ArrowLeft size={12} aria-hidden="true" /> Retour bibliothèque
            </Button>
          }
          title="Chargement…"
        />
        <p className="new-work-loading">Chargement de l’œuvre…</p>
      </Page>
    )
  }

  if (isEditMode && workQuery.isError) {
    return (
      <Page width="wide">
        <Page.Header
          eyebrow={
            <Button variant="quiet" size="sm" type="button" onClick={() => navigate('/works')}>
              <ArrowLeft size={12} aria-hidden="true" /> Retour bibliothèque
            </Button>
          }
          title="Œuvre introuvable"
        />
        <p className="new-work-loading">
          {(workQuery.error as { detail?: string } | null)?.detail ?? "L'œuvre n'existe pas."}
        </p>
      </Page>
    )
  }

  const headerTitle = isEditMode ? workQuery.data?.title_fr || 'Édition' : 'Nouvelle œuvre'
  const headerEyebrow = isEditMode ? `Inv. nº ${workId} · 2026` : 'Nouvelle entrée au catalogue'

  return (
    <Page width="wide">
      <FormProvider {...methods}>
        {/* FormProvider wrappe `<Page.Header>` pour que le sous-composant
            <CompletionProgress> (rendu dans `meta`) puisse consommer
            useWatch() depuis le contexte. */}
        <Page.Header
          eyebrow={
            <span className="new-work-eyebrow">
              <Button variant="quiet" size="sm" type="button" onClick={() => navigate('/works')}>
                <ArrowLeft size={12} aria-hidden="true" /> Bibliothèque
              </Button>
              <span className="new-work-eyebrow-sep" aria-hidden="true">
                ·
              </span>
              <span className="new-work-eyebrow-inv">{headerEyebrow}</span>
            </span>
          }
          title={headerTitle}
          meta={
            <div className="new-work-meta-row">
              <CompletionProgress
                hasMedia={heroMedia !== null}
                storyHasCorrections={storyHasCorrections}
                descriptionHasCorrections={descriptionHasCorrections}
              />
              {statusLabel && (
                <span className={`new-work-status new-work-status--${status}`}>
                  <span className="new-work-status-dot" aria-hidden="true" />
                  {statusLabel}
                </span>
              )}
              {visionPending && (
                <span className="new-work-status">
                  <Sparkles size={12} aria-hidden="true" /> Analyse visuelle en cours…
                </span>
              )}
            </div>
          }
        />

        {/* Pas de `<form>` : l'éditeur fonctionne en autosave continu, il n'y
            a pas de bouton submit. Les sous-formulaires (modal Rephrase) ont
            leur propre `<form>` quand nécessaire. */}
        <div className="new-work-grid">
          {/* ── Cartel preview (gauche) ──────────────────────────────── */}
          <aside className="new-work-cartel" aria-label="Aperçu cartel">
            <CartelPreview
              heroMinioKey={heroMedia?.minio_key ?? null}
              emotionalIntentOptions={emotionalIntentOptions}
            />
            <p className="new-work-cartel-caption">
              Aperçu · fiche de catalogue · se met à jour pendant la saisie
            </p>
          </aside>

          {/* ── Formulaire (droite) ──────────────────────────────────── */}
          <div className="new-work-form-stack">
            <FormSection
              number="01"
              title="Identité"
              sub="Comment cette œuvre s'appelle, où elle se range."
            >
              <FormField
                name="title_fr"
                label="Titre"
                required
                placeholder="« Sillage », « Le silence après la pluie »…"
                helper="Sera traduit automatiquement en anglais (Claude Vision)."
                focusOnMount={!isEditMode}
              />

              <Controller
                control={control}
                name="series_id"
                render={({ field }) => {
                  const selectedSeriesId =
                    field.value !== undefined && field.value !== null ? Number(field.value) : null
                  const selectedSeries = selectedSeriesId
                    ? seriesOptions.find((s) => s.id === selectedSeriesId)
                    : null
                  return (
                    <>
                      <Field
                        label="Série"
                        helper="Optionnel — regroupe plusieurs œuvres. La position est gérée par le panneau ci-dessous."
                        labelActions={
                          <Button
                            type="button"
                            variant="quiet"
                            size="sm"
                            onClick={() => setSeriesModalOpen(true)}
                          >
                            <Plus size={14} aria-hidden="true" />
                            Nouvelle série
                          </Button>
                        }
                      >
                        {(controlProps) => (
                          <Select
                            {...controlProps}
                            value={field.value !== undefined ? String(field.value) : ''}
                            onChange={(e) => {
                              const v = e.target.value
                              field.onChange(v ? Number(v) : undefined)
                            }}
                          >
                            <option value="">— Aucune série —</option>
                            {seriesOptions.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name_fr}
                              </option>
                            ))}
                          </Select>
                        )}
                      </Field>
                      {selectedSeriesId !== null && selectedSeries && (
                        <SeriesReorderPanel
                          seriesId={selectedSeriesId}
                          seriesName={selectedSeries.name_fr}
                          currentWorkId={workId}
                        />
                      )}
                    </>
                  )
                }}
              />

              <Controller
                control={control}
                name="technique_tags"
                render={({ field }) => (
                  <Field label="Technique" helper="Aquarelle, encre, technique mixte…">
                    {(controlProps) => (
                      <TagPicker
                        {...controlProps}
                        value={(field.value ?? []).map((slug) => ({
                          id: slug,
                          slug,
                          label: slug,
                          kind: 'technique',
                        }))}
                        onChange={(next) => field.onChange(next.map((t) => t.slug))}
                        onSearch={async (q) => {
                          const data = await tagsApi.list({
                            q,
                            kind: 'technique',
                            limit: 20,
                          })
                          return data.map(tagResponseToTag)
                        }}
                        onCreate={async (label) => {
                          const created = await tagsApi.create({
                            label_fr: label,
                            kind: 'technique',
                          })
                          return tagResponseToTag(created)
                        }}
                        kind="technique"
                        placeholder="Ajouter une technique…"
                        ariaLabel="Techniques"
                      />
                    )}
                  </Field>
                )}
              />

              <FormField
                name="created_at_artistic"
                label="Date de création"
                type="date"
                helper="Optionnel — sinon, prise auto sur EXIF de la 1re image."
              />
            </FormSection>

            <FormSection
              number="02"
              title="Photographies"
              sub="Le cartel sera bâti à partir de la première image."
            >
              {galleryMedia.length > 0 && (
                <ul className="new-work-thumbs" role="list">
                  {galleryMedia.map((m, idx) => {
                    const sourceId = m.source_media_id
                    const kebabItems: KebabMenuItem[] = sourceId
                      ? [
                          {
                            id: 'delete',
                            label: 'Supprimer cette photographie',
                            icon: Trash2,
                            variant: 'danger',
                            disabled: deleteMediaMutation.isPending,
                            onSelect: () => {
                              void handleDeleteMedia(sourceId, idx)
                            },
                          },
                        ]
                      : []
                    return (
                      <li
                        key={m.id}
                        className={
                          idx === 0 ? 'new-work-thumb new-work-thumb--primary' : 'new-work-thumb'
                        }
                      >
                        <MediaImage
                          minioKey={m.minio_key}
                          alt={`Photographie ${idx + 1} · ${workQuery.data?.title_fr ?? ''}`}
                        />
                        {idx === 0 && (
                          <span className="new-work-thumb-badge" aria-label="Image principale">
                            1ʳᵉ
                          </span>
                        )}
                        {kebabItems.length > 0 && (
                          <div className="new-work-thumb-actions">
                            <KebabMenu
                              ariaLabel={`Actions sur la photographie ${idx + 1}`}
                              buttonVariant="overlay"
                              items={kebabItems}
                            />
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}

              <Field
                label="Fichiers (JPG ou RAW)"
                hideLabel
                helper="JPG ou RAW (Canon CR2/CR3, Nikon NEF, Sony ARW, Adobe DNG, Fujifilm RAF, Olympus ORF). Max 10 fichiers, 60 Mo chacun, ≥ 1500px petit côté, ≤ 12000px grand côté."
              >
                {() =>
                  workId === null ? (
                    // Mode création : la dropzone est branchée sur le buffer
                    // `pendingFiles` (l'utilisatrice voit ses fichiers en
                    // attente et peut en retirer un avant la création).
                    <FileDropZone
                      value={pendingFiles}
                      onChange={setPendingFiles}
                      accept={ACCEPTED_FILE_EXTENSIONS}
                      maxFiles={10}
                    />
                  ) : (
                    // Mode édition : chaque drop est enqueue immédiatement
                    // dans la pile globale d'uploads ; la dropzone reste
                    // visuellement vide (la progression vit dans l'overlay).
                    <FileDropZone
                      value={[]}
                      onChange={handleFilesChange}
                      accept={ACCEPTED_FILE_EXTENSIONS}
                      maxFiles={10}
                    />
                  )
                }
              </Field>
              {pendingFiles.length > 0 && (
                <p className="new-work-pending-hint">
                  {pendingFiles.length === 1
                    ? '1 fichier en attente — sera attaché dès la création.'
                    : `${pendingFiles.length} fichiers en attente — seront attachés dès la création.`}
                </p>
              )}
            </FormSection>

            <FormSection
              number="03"
              title="Intention"
              sub="Ce qui rendra les drafts plateformes vivants — pas génériques."
            >
              <Controller
                control={control}
                name="emotional_intent"
                render={({ field }) => (
                  <Field
                    label="Intention émotionnelle"
                    helper="2-3 max. Ce que la personne devrait ressentir."
                  >
                    {(controlProps) => (
                      <ChipMultiSelect<string>
                        {...controlProps}
                        options={emotionalIntentOptions}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        max={3}
                      />
                    )}
                  </Field>
                )}
              />

              <Controller
                control={control}
                name="target_audience"
                render={({ field }) => (
                  <Field label="Public cible" helper="1-3 choix. Oriente la voix d'écriture.">
                    {(controlProps) => (
                      <ChipMultiSelect<string>
                        {...controlProps}
                        options={targetAudienceOptions}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        max={3}
                      />
                    )}
                  </Field>
                )}
              />

              <Controller
                control={control}
                name="story_fr"
                render={({ field, fieldState }) => (
                  <Field
                    label="Story"
                    error={fieldState.error?.message}
                    helper="L'anecdote qui donne l'âme. Max 500 caractères. Sera traduit en anglais."
                    labelActions={
                      <>
                        <FieldThresholdHint
                          name="story_fr"
                          minChars={PORTFOLIO_PUBLISH_STORY_FR_MIN_CHARS}
                          inline
                        />
                        <ProofreadHint
                          name="story_fr"
                          onOpenInModal={(text) =>
                            openCorrectionFromHint('story_fr', 'Story', text)
                          }
                          onHasCorrectionsChange={setStoryHasCorrections}
                          inline
                        />
                        <Button
                          variant="quiet"
                          size="sm"
                          type="button"
                          onClick={() => openRephrase('story_fr', 'Story')}
                          loading={rephraseMutation.isPending && rephraseOpen?.field === 'story_fr'}
                          aria-label="Aide-moi à formuler"
                        >
                          <Sparkles size={14} aria-hidden="true" />
                          Aide-moi
                        </Button>
                      </>
                    }
                  >
                    {(controlProps) => (
                      <Textarea
                        {...controlProps}
                        {...field}
                        rows={2}
                        maxLength={500}
                        autosize
                        className={
                          storyHasCorrections ? 'new-work-textarea--has-corrections' : undefined
                        }
                        placeholder="« Peinte pendant la canicule, fenêtre ouverte sur le jardin »…"
                      />
                    )}
                  </Field>
                )}
              />

              <Controller
                control={control}
                name="description_fr"
                render={({ field, fieldState }) => (
                  <Field
                    label="Description"
                    error={fieldState.error?.message}
                    helper="Ce que tu raconterais à quelqu'un qui découvre l'œuvre. Sera traduit en anglais."
                    labelActions={
                      <>
                        <FieldThresholdHint
                          name="description_fr"
                          minChars={PORTFOLIO_PUBLISH_DESCRIPTION_FR_MIN_CHARS}
                          inline
                        />
                        <ProofreadHint
                          name="description_fr"
                          onOpenInModal={(text) =>
                            openCorrectionFromHint('description_fr', 'Description', text)
                          }
                          onHasCorrectionsChange={setDescriptionHasCorrections}
                          inline
                        />
                        <Button
                          variant="quiet"
                          size="sm"
                          type="button"
                          onClick={() => openRephrase('description_fr', 'Description')}
                          loading={
                            rephraseMutation.isPending && rephraseOpen?.field === 'description_fr'
                          }
                          aria-label="Aide-moi à formuler"
                        >
                          <Sparkles size={14} aria-hidden="true" />
                          Aide-moi
                        </Button>
                      </>
                    }
                  >
                    {(controlProps) => (
                      <Textarea
                        {...controlProps}
                        {...field}
                        rows={4}
                        autosize
                        className={
                          descriptionHasCorrections
                            ? 'new-work-textarea--has-corrections'
                            : undefined
                        }
                        placeholder="Ce que tu raconterais à quelqu'un qui découvre l'œuvre."
                      />
                    )}
                  </Field>
                )}
              />
            </FormSection>

            <FormSection
              number="04"
              title="Catalogage"
              sub="Pour retrouver et regrouper l'œuvre dans le catalogue."
            >
              <Controller
                control={control}
                name="influences"
                render={({ field }) => (
                  <Field
                    label="Influences artistiques"
                    helper="Noms propres — non traduits. Hammershøi, Morandi, Hiroshige…"
                  >
                    {(controlProps) => (
                      <TagPicker
                        {...controlProps}
                        value={(field.value ?? []).map((label) => ({
                          id: label,
                          slug: label,
                          label,
                          kind: 'influence',
                        }))}
                        onChange={(next) => field.onChange(next.map((t) => t.label))}
                        onSearch={async (q) => {
                          const data = await tagsApi.list({
                            q,
                            kind: 'influence',
                            limit: 20,
                          })
                          return data.map(tagResponseToTag)
                        }}
                        onCreate={async (label) => {
                          const created = await tagsApi.create({
                            label_fr: label,
                            kind: 'influence',
                          })
                          return tagResponseToTag(created)
                        }}
                        kind="influence"
                        placeholder="Hammershøi, Morandi…"
                        ariaLabel="Influences"
                      />
                    )}
                  </Field>
                )}
              />

              <Field
                label="Tags libres"
                helper="Thèmes, couleurs, ambiances. Réutilisés sur l'ensemble du catalogue."
              >
                {(controlProps) => (
                  <TagPicker
                    {...controlProps}
                    value={extraTags}
                    onChange={setExtraTags}
                    onSearch={async (q, kind) => {
                      const data = await tagsApi.list({ q, kind, limit: 20 })
                      return data.map(tagResponseToTag)
                    }}
                    onCreate={async (label, kind) => {
                      const created = await tagsApi.create({
                        label_fr: label,
                        kind: kind ?? 'theme',
                      })
                      return tagResponseToTag(created)
                    }}
                    placeholder="Ajouter un tag…"
                    ariaLabel="Tags"
                  />
                )}
              </Field>
            </FormSection>

            <FormSection
              number="05"
              title="Notes d'atelier"
              sub="FR uniquement, atelier interne, jamais publié."
            >
              <FormTextarea
                name="notes"
                label="Notes"
                hideLabel
                rows={3}
                placeholder="Ne sera jamais publié. Pour ton usage interne."
              />
              <ProofreadHint
                name="notes"
                onOpenInModal={(text) => openCorrectionFromHint('notes', 'Notes', text)}
              />
            </FormSection>
          </div>
        </div>
      </FormProvider>

      {/* ── Modal "Aide-moi à formuler" — reformulation Claude (S3) ───── */}
      <Modal
        isOpen={rephraseOpen !== null}
        onClose={() => setRephraseOpen(null)}
        title={rephraseOpen ? `Reformuler la ${rephraseOpen.label.toLowerCase()}` : ''}
        kicker="Carte « Corrigé » : juste les fautes. Reformulations : changement de voix."
        size="form"
      >
        <Modal.Body>
          {rephraseMutation.isPending && <p className="ui-text-muted">Claude réfléchit…</p>}
          {!rephraseMutation.isPending && rephraseCorrection && (
            <ul className="new-work-rephrase-list" role="list">
              {rephraseCorrection.hasChanges ? (
                <li className="new-work-rephrase-item new-work-rephrase-item-corrected">
                  <span className="new-work-rephrase-badge">Corrigé</span>
                  <p className="new-work-rephrase-text">{rephraseCorrection.text}</p>
                  <Button
                    variant="primary"
                    size="sm"
                    type="button"
                    onClick={() => acceptRephrase(rephraseCorrection.text)}
                  >
                    Appliquer la correction
                  </Button>
                </li>
              ) : (
                <li className="new-work-rephrase-item new-work-rephrase-item-clean">
                  <p className="ui-text-muted">✓ Texte déjà correct — pas de faute détectée.</p>
                </li>
              )}
              {rephraseVariants.map((variant, idx) => (
                <li key={idx} className="new-work-rephrase-item">
                  <span className="new-work-rephrase-variant-label">Reformulation {idx + 1}</span>
                  <p className="new-work-rephrase-text">{variant}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => acceptRephrase(variant)}
                  >
                    Utiliser cette version
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" type="button" onClick={() => setRephraseOpen(null)}>
            Garder ma version
          </Button>
        </Modal.Footer>
      </Modal>

      <SeriesCreateModal
        isOpen={seriesModalOpen}
        onClose={() => setSeriesModalOpen(false)}
        isSubmitting={createSeriesMutation.isPending}
        onSubmit={async (payload) => createSeriesMutation.mutateAsync(payload)}
        onCreated={(created) => {
          methods.setValue('series_id', created.id, {
            shouldDirty: true,
            shouldValidate: true,
          })
          setSeriesModalOpen(false)
          toast.success(`Série « ${created.name_fr} » créée`)
        }}
      />
    </Page>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Sous-composants
// ──────────────────────────────────────────────────────────────────────────

interface CompletionProgressProps {
  hasMedia: boolean
  storyHasCorrections: boolean
  descriptionHasCorrections: boolean
}

/** Barre segmentée 6 segments — image, titre, technique, intent, story, description.
 *  Story/description exigent en plus l'absence de corrections orthographiques en
 *  attente : tant que `<ProofreadHint>` propose un fix, le segment reste vide. */
function CompletionProgress({
  hasMedia,
  storyHasCorrections,
  descriptionHasCorrections,
}: CompletionProgressProps) {
  const { control } = useFormContext<WorkEditorForm>()
  const values = useWatch({ control })
  const filled = (s?: string | null) => Boolean(s && s.trim().length > 0)
  const completion = [
    filled(values.title_fr),
    hasMedia,
    (values.technique_tags?.length ?? 0) > 0,
    (values.emotional_intent?.length ?? 0) > 0,
    filled(values.story_fr) && !storyHasCorrections,
    filled(values.description_fr) && !descriptionHasCorrections,
  ].filter(Boolean).length

  return (
    <span
      className="new-work-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={COMPLETION_TOTAL}
      aria-valuenow={completion}
      aria-label="Progression de la fiche"
    >
      <span className="new-work-progress-track" aria-hidden="true">
        {Array.from({ length: COMPLETION_TOTAL }).map((_, i) => (
          <span
            key={i}
            className={
              i < completion
                ? 'new-work-progress-segment new-work-progress-segment--filled'
                : 'new-work-progress-segment'
            }
          />
        ))}
      </span>
      <span className="new-work-progress-label">
        {completion}/{COMPLETION_TOTAL} éléments renseignés
      </span>
    </span>
  )
}

interface CartelPreviewProps {
  heroMinioKey: string | null
  emotionalIntentOptions: { value: string; label: string }[]
}

/** Cartel-de-musée live — driven by RHF watch. Read-only (upload est en bloc 02). */
function CartelPreview({ heroMinioKey, emotionalIntentOptions }: CartelPreviewProps) {
  const { control } = useFormContext<WorkEditorForm>()
  const values = useWatch({ control })
  const filled = (s?: string | null) => Boolean(s && s.trim().length > 0)

  const seriesId =
    values.series_id !== undefined && values.series_id !== null ? Number(values.series_id) : null
  const seriesWorksQuery = useSeriesWorks(seriesId)
  const seriesTotal = seriesWorksQuery.data?.length ?? 0

  const emotionalLabels = useMemo(() => {
    const lookup = new Map(emotionalIntentOptions.map((o) => [o.value, o.label]))
    return (values.emotional_intent ?? [])
      .map((slug) => lookup.get(slug))
      .filter((x): x is string => Boolean(x))
  }, [values.emotional_intent, emotionalIntentOptions])

  const influencesLabels = (values.influences ?? []).filter(Boolean)
  const techniqueLabels = (values.technique_tags ?? []).filter(Boolean)

  return (
    <div className="new-work-cartel-card">
      <div className="new-work-cartel-image">
        {heroMinioKey ? (
          <MediaImage minioKey={heroMinioKey} alt={values.title_fr || 'Aperçu de l’œuvre'} />
        ) : (
          <div className="new-work-cartel-image-empty">
            <ImageIcon className="new-work-cartel-image-mark" aria-hidden="true" strokeWidth={1} />
            <span className="new-work-cartel-image-prompt">Aperçu de l'œuvre</span>
            <span className="new-work-cartel-image-hint">Dépose une photo dans Bloc 02 ↘</span>
          </div>
        )}
      </div>

      <div className="new-work-cartel-body">
        <p className="new-work-cartel-author">Stéphanie</p>
        <h2
          className={
            filled(values.title_fr)
              ? 'new-work-cartel-title'
              : 'new-work-cartel-title new-work-cartel-title--placeholder'
          }
        >
          {filled(values.title_fr) ? `« ${values.title_fr} »` : 'Sans titre'}
        </h2>

        {values.series_position && seriesTotal > 0 && (
          <p className="new-work-cartel-series">
            <span>
              pièce {values.series_position} / {seriesTotal}
            </span>
          </p>
        )}

        <div className="new-work-cartel-rule" aria-hidden="true" />

        <div className="new-work-cartel-meta">
          {techniqueLabels.length > 0 ? (
            <p className="new-work-cartel-meta-line">{techniqueLabels.join(', ')}</p>
          ) : (
            <p className="new-work-cartel-meta-line new-work-cartel-meta-line--missing">
              technique à renseigner
            </p>
          )}
          {filled(values.created_at_artistic) && (
            <p className="new-work-cartel-meta-date">{values.created_at_artistic}</p>
          )}
        </div>

        {(filled(values.story_fr) || filled(values.description_fr)) && (
          <>
            <div className="new-work-cartel-rule new-work-cartel-rule--soft" aria-hidden="true" />
            {filled(values.story_fr) && (
              <p className="new-work-cartel-story">« {values.story_fr} »</p>
            )}
            {filled(values.description_fr) && (
              <p className="new-work-cartel-description">{values.description_fr}</p>
            )}
          </>
        )}

        {emotionalLabels.length > 0 && (
          <div className="new-work-cartel-moods">
            {emotionalLabels.map((m) => (
              <span key={m} className="new-work-cartel-mood">
                — {m.toLowerCase()}
              </span>
            ))}
          </div>
        )}

        {influencesLabels.length > 0 && (
          <p className="new-work-cartel-influences">cf. {influencesLabels.join(', ')}</p>
        )}
      </div>
    </div>
  )
}
